import type { ReactNode } from 'react';
import { Loader2, Settings } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { CommandGroup, CommandItem } from '../ui/command';
import { cn } from '../../lib/utils';

export interface MentionRowProps {
  /**
   * The kind's icon (a lucide glyph, emoji, or custom node). Rendered in a
   * fixed-width slot so every row's name starts at the same x regardless of
   * which kind it is.
   */
  icon: ReactNode;
  /** Primary label: the thing's name, and the main scan target. */
  name: ReactNode;
  /**
   * Optional hover content describing the item (multi-line allowed). When set,
   * the whole row becomes a hover trigger. Keeps rows to a single line — the
   * detail lives on hover, not inline. Use {@link MentionDetail} for a uniform
   * body.
   */
  details?: ReactNode;
  /**
   * Optional trailing control for the row (e.g. an edit cog). Rendered outside
   * the hover trigger so pointing at the control does not also raise the
   * details card over it. A control here must stop its own click from
   * propagating, or selecting it also selects the row.
   */
  action?: ReactNode;
  /** Extra classes for the name span. */
  className?: string;
}

/**
 * One row of the launch `@`-mention menu. The row is a single line that leads
 * with the name (its kind reads from the surrounding group heading); hovering
 * reveals brief details. Every provider renders this as the child of a
 * `CommandItem` so the whole menu shares one layout.
 */
export function MentionRow({ icon, name, details, action, className }: MentionRowProps) {
  const row = (
    <span className={cn('flex items-center gap-2', action ? 'min-w-0 flex-1' : 'w-full')}>
      <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-3.5">
        {icon}
      </span>
      <span className={cn('min-w-0 flex-1 truncate', className)}>{name}</span>
    </span>
  );

  const body = details ? (
    <HoverCard openDelay={250} closeDelay={80}>
      <HoverCardTrigger asChild>{row}</HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="center"
        sideOffset={8}
        className="w-max min-w-[10.5rem] max-w-56 p-2.5"
      >
        {details}
      </HoverCardContent>
    </HoverCard>
  ) : (
    row
  );

  if (!action) return body;

  return (
    <span className="flex w-full items-center gap-1">
      {body}
      <span className="shrink-0">{action}</span>
    </span>
  );
}

export interface MentionLoadingRowProps {
  /** What is loading, e.g. "Loading repositories…". */
  label?: string;
  /** When set, wraps the row in a `CommandGroup` with this heading — mirrors
   *  how providers group their items in the flattened root search. */
  groupHeading?: string;
}

/**
 * A non-selectable "loading" row for the launch `@`-mention menu. Providers
 * render this while their data is still fetching and they have nothing to show
 * yet, instead of returning `null`. Returning `null` collapses a scoped view
 * (e.g. `@repo:`) to the menu's generic "No matches." empty state, which reads
 * as "nothing here" rather than "still loading".
 *
 * The row is `disabled` so the menu's keyboard navigation skips it (the menu
 * treats `aria-disabled` items as non-selectable) while still satisfying cmdk's
 * item count, which keeps the empty state hidden. The `value` embeds the label
 * so multiple providers' loading rows stay distinct in the flattened view.
 */
export function MentionLoadingRow({ label = 'Loading…', groupHeading }: MentionLoadingRowProps) {
  const row = (
    <CommandItem value={`__mention-loading:${label}`} disabled className="text-muted-foreground">
      <span className="flex w-full items-center gap-2">
        <Loader2 className="size-3.5 shrink-0 animate-spin" />
        <span className="truncate">{label}</span>
      </span>
    </CommandItem>
  );
  return groupHeading ? <CommandGroup heading={groupHeading}>{row}</CommandGroup> : row;
}

export interface MentionNoticeRowProps {
  /** Glyph for the notice — an alert or the provider's own brand mark. */
  icon: ReactNode;
  /** What is missing, e.g. "GitHub not connected". Kept to one short line. */
  label: string;
  /**
   * Why the row is a notice, e.g. "No access". Muted and trailing, so the
   * label can stay the thing's own name and the reason still reads at a
   * glance. Two or three words: it shares the line with {@link
   * MentionNoticeRowProps.actionLabel} and truncates after the label.
   */
  hint?: ReactNode;
  /** The remedy, rendered as a trailing hint, e.g. "Connect". */
  actionLabel?: string;
  /** Runs when the row is chosen. Omit for a purely informational notice. */
  onSelect?: () => void;
  /** Wraps the row in a `CommandGroup` with this heading, like the other rows. */
  groupHeading?: string;
  /**
   * cmdk item value, defaulting to one derived from the label. Pass an id
   * alongside the label when several notices can share a label (two
   * servers of the same name), so cmdk keeps them as distinct items.
   */
  value?: string;
  /** Stable hook for tests; also carried as `data-testid` on the item. */
  testId?: string;
}

/**
 * A "something is missing" row for the launch `@`-mention menu — the provider
 * has nothing to list because a prerequisite is unmet (a credential is
 * unlinked, an integration is not set up), not because the search matched
 * nothing.
 *
 * Deliberately NOT a {@link MentionRow}: a result row leads with a name and
 * carries a hover card, so a notice built from one reads as "here is a repo"
 * rather than "you need to do something". This mirrors {@link MentionLoadingRow}
 * instead — compact, muted, icon-led — so the menu has one consistent shape for
 * status rows, distinct from results.
 *
 * Unlike the loading row this one stays selectable when `onSelect` is given, so
 * the fix is one keystroke away; the trailing `actionLabel` advertises it.
 */
export function MentionNoticeRow({
  icon,
  label,
  hint,
  actionLabel,
  onSelect,
  groupHeading,
  value,
  testId,
}: MentionNoticeRowProps) {
  const row = (
    <CommandItem
      value={value ?? `__mention-notice:${label}`}
      disabled={!onSelect}
      onSelect={onSelect}
      className="text-muted-foreground"
      data-testid={testId}
    >
      <span className="flex w-full items-center gap-2">
        <span className="flex size-4 shrink-0 items-center justify-center [&_svg]:size-3.5">
          {icon}
        </span>
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {hint ? <span className="shrink-0 text-2xs text-muted-foreground/70">{hint}</span> : null}
        {actionLabel ? (
          <span className="shrink-0 text-xs font-medium text-primary">{actionLabel}</span>
        ) : null}
      </span>
    </CommandItem>
  );
  return groupHeading ? <CommandGroup heading={groupHeading}>{row}</CommandGroup> : row;
}

export interface MentionSetupRowProps {
  /** What to do, e.g. "Connect Sentry to search issues". */
  label: string;
  /** Navigate to the integration's setup page (the caller closes the menu). */
  onSelect: () => void;
  /** Wraps the row in a `CommandGroup` with this heading, mirroring providers. */
  groupHeading?: string;
}

/**
 * The agent-context providers' "set this integration up first" row: a
 * {@link MentionNoticeRow} preset rendered instead of results when the
 * provider's integration is not configured for the org (no connection, app
 * not installed), so the search prompts the user toward setup rather than
 * showing an inexplicably empty category. Selecting it should navigate to
 * the integration's settings page and close the menu.
 */
export function MentionSetupRow({ label, onSelect, groupHeading }: MentionSetupRowProps) {
  return (
    <MentionNoticeRow
      icon={<Settings />}
      label={label}
      actionLabel="Set up"
      onSelect={onSelect}
      groupHeading={groupHeading}
      testId="mention-setup-row"
    />
  );
}

export interface MentionDetailFact {
  label: string;
  value: ReactNode;
}

export interface MentionDetailProps {
  name: ReactNode;
  /** Short kind label, e.g. "Repository". */
  kind?: ReactNode;
  /** One or two sentences on what the thing is. */
  description?: ReactNode;
  /** A few key/value specifics (owner, endpoint, agent type, ...). */
  facts?: MentionDetailFact[];
}

/**
 * Uniform body for a mention row's hover tooltip. Matches ResourcePreview
 * density: name, kind under it, then short facts. The card hugs content
 * (see MentionRow hover chrome) so a single fact does not sit in a wide empty panel.
 */
export function MentionDetail({ name, kind, description, facts }: MentionDetailProps) {
  return (
    <div className="min-w-0 space-y-2" data-testid="mention-detail">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium leading-tight">{name}</p>
        {kind ? (
          <p className="truncate text-[11px] leading-snug text-muted-foreground">{kind}</p>
        ) : null}
      </div>
      {description ? (
        <p className="text-[12px] leading-snug text-muted-foreground">{description}</p>
      ) : null}
      {facts && facts.length > 0 ? (
        <dl
          className={cn(
            'grid grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 gap-y-0.5 text-[11px] leading-snug',
            description && 'border-t border-border/60 pt-2',
          )}
        >
          {facts.map((f) => (
            <div key={f.label} className="contents">
              <dt className="text-muted-foreground">{f.label}</dt>
              <dd className="min-w-0 truncate font-medium text-foreground">{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}
